import { DropdownPillComboboxSingleValueProps } from 'ui-kit/Combobox/DropdownPillComboboxSingleValue';
import {
  DropdownPillComboboxMultiValueProps,
  Option,
} from 'ui-kit/Combobox/DropdownPillComboboxMultiValue';
import { isString, isType } from 'utils/typeUtils';

import { FilterableField, FilterOperator } from '__generated__/globalTypes';
// Option itself is Combobox's — re-exported here so existing importers of
// filters.types keep working, and so ui-kit's barrel doesn't end up with two
// same-named-but-separately-declared Option types.
export type { Option };
export const isOption = isType<Option>({ value: isString, label: isString });

export type RangeFilterConfigEntry = {
  displayName: string;
  type: 'range';
  min: number;
  max: number;
  inputFormat?: (value: string) => number;
  displayFormat?: (value: number) => string;
  prepare?: (value: number) => number;
  percentage?: boolean;
  // Rounds the slider to this many raw units per step (see RangeSlider's
  // step prop) — for a field whose meaningful granularity isn't 1/0.05/0.25
  // (what integer/decimal give you), e.g. a bps field that should move in
  // whole-percent (step: 100) or quarter-point (step: 25) increments.
  step?: number;
  integer?: boolean;
  decimal?: boolean;
};

export type BooleanFilterConfigEntry = {
  displayName: string;
  truthyLabel: string;
  falseyLabel: string;
  type: 'boolean';
};

export type ListInputFilterConfigEntry = {
  displayName: string;
  type: 'list-input';
};

export type DateRangeFilterConfigEntry = {
  displayName: string;
  type: 'date-range';
  min: number;
  max: number;
};

export type BaseFiltersConfig = {
  [key: string]:
    | RangeFilterConfigEntry
    | SingleSelectFilterConfigEntry<Option>
    | BooleanFilterConfigEntry
    | MultiSelectFilterConfigEntry<Option>
    | ListInputFilterConfigEntry
    | DateRangeFilterConfigEntry;
};

export type SingleSelectFilterConfigEntry<Op extends Option> = {
  displayName: string;
  type: 'select-single';
  filterOptions: Op[];
} & Required<
  Pick<
    DropdownPillComboboxSingleValueProps<Op>,
    'valueToString' | 'getOptionLabel' | 'getOptionValue'
  >
>;

export type MultiSelectFilterConfigEntry<Op extends Option> = {
  displayName: string;
  filterableFieldName: FilterableField;
  type: 'select-multi';
  filterOptions: Op[];
} & Required<
  Pick<
    DropdownPillComboboxMultiValueProps<Op>,
    'valueToString' | 'getOptionLabel' | 'getOptionValue'
  >
>;

export type RangeFilter<T extends BaseFiltersConfig> = {
  name: keyof T;
  min: number;
  max: number;
};

export type DateRangeFilter<T extends BaseFiltersConfig> = {
  name: keyof T;
  min: number;
  max: number;
};

export type SingleSelectFilter<T extends BaseFiltersConfig> = {
  name: keyof T;
  value: string | null;
};

export type MultiSelectFilter<T extends BaseFiltersConfig> = {
  name: keyof T;
  value: string[] | null;
};

export type BooleanFilter<T extends BaseFiltersConfig> = {
  name: keyof T;
  value: boolean;
};

export type ListInputFilter<T extends BaseFiltersConfig> = {
  name: keyof T;
  values: string[];
};

export type FilterValue<T extends BaseFiltersConfig> =
  | RangeFilter<T>
  | SingleSelectFilter<T>
  | BooleanFilter<T>
  | MultiSelectFilter<T>
  | ListInputFilter<T>
  | DateRangeFilter<T>;

export type QueryFilter<T extends BaseFiltersConfig> = {
  field_name: keyof T;
  operator: FilterOperator;
  operand?: string | null;
  operandList?: string[] | null;
};
